import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Sparkles,
  MessageSquare,
  Calendar,
  Users,
  Map,
  Heart,
  ArrowRight,
  CheckCircle,
  Send,
  Plane,
  PartyPopper,
  Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Share Your Vision",
    description: "Tell us about your dream trip. Where do you want to go? What experiences matter most to you? We'll start with a conversation to understand your travel style, interests, and must-haves."
  },
  {
    number: "02",
    icon: Map,
    title: "We Craft Your Itinerary",
    description: "Our travel experts design a personalized itinerary just for you. We'll incorporate your preferences, suggest hidden gems, and balance must-see highlights with authentic local experiences."
  },
  {
    number: "03",
    icon: Calendar,
    title: "Refine & Confirm",
    description: "Review your custom itinerary and let us know if you'd like any adjustments. Want to add an extra day? Swap an activity? We'll fine-tune until it's perfect."
  },
  {
    number: "04",
    icon: Plane,
    title: "Pack & Play",
    description: "Once your trip is confirmed, we handle all the logistics. You'll receive detailed travel documents, and we're available 24/7 during your journey for support."
  }
];

const customizationExamples = [
  {
    icon: Users,
    title: "Private Group Tours",
    description: "Take any of our existing itineraries and run it exclusively for your group. Perfect for friend groups, families, or organizations wanting a shared adventure.",
    examples: ["Bachelor/Bachelorette trips", "Family reunions", "Corporate retreats", "Friend group getaways"]
  },
  {
    icon: PartyPopper,
    title: "Special Occasions",
    description: "Celebrating a milestone? Let us design a trip that marks the moment in an unforgettable way.",
    examples: ["Birthday celebrations", "Anniversary trips", "Graduation rewards", "Retirement adventures"]
  },
  {
    icon: Calendar,
    title: "Custom Dates",
    description: "Our scheduled group tours don't align with your calendar? We can run any itinerary on dates that work for you.",
    examples: ["School vacation timing", "Work schedule flexibility", "Avoiding peak seasons", "Holiday travel"]
  },
  {
    icon: Briefcase,
    title: "Extended Itineraries",
    description: "Want more time to explore? We can extend any trip, add destinations, or combine multiple itineraries into one epic journey.",
    examples: ["Add extra destinations", "Combine Egypt + Morocco", "Extended stays in favorite cities", "Pre/post trip extensions"]
  }
];

export default function CustomizeTripPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    tripType: '',
    destination: '',
    groupSize: '',
    preferredDates: '',
    budget: '',
    occasion: '',
    details: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.details) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        type: 'custom_trip_request'
      });
      setSubmitted(true);
      toast.success('Request submitted! We\'ll be in touch soon.');
    } catch (err) {
      toast.error('Failed to submit. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-secondary/10 to-transparent pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4" />
            Your Trip, Your Way
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Customize Your Adventure
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Our itineraries are just the beginning. Whether you want to travel on your own dates, 
            bring your own group, or create something entirely unique — we're here to make it happen.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* How Customization Works */}
        <section className="mb-20">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary text-center mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className="h-full text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-secondary" />
                  <CardContent className="pt-8 pb-6">
                    <span className="text-4xl font-heading font-bold text-secondary/20 block mb-2">
                      {step.number}
                    </span>
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-6 w-6 text-secondary" />
                    </div>
                    <h3 className="font-heading font-semibold text-primary mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Customization Examples */}
        <section className="mb-20">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary text-center mb-4">
            Ways to Customize
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Here are some of the ways travelers have customized their trips with us
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {customizationExamples.map((example, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                        <example.icon className="h-5 w-5 text-secondary" />
                      </div>
                      <CardTitle className="text-lg">{example.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm mb-4">
                      {example.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {example.examples.map((ex, i) => (
                        <span key={i} className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                          {ex}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Request Form */}
        <section id="request-form">
          <Card className="max-w-3xl mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl flex items-center justify-center gap-2">
                <Heart className="h-6 w-6 text-secondary" />
                Start Your Custom Trip Request
              </CardTitle>
              <p className="text-muted-foreground">
                Share your travel dreams and we'll get back to you within 24-48 hours
              </p>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-primary mb-2">
                    Request Received!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    We're excited to help you plan your custom adventure. Our team will review your request 
                    and reach out within 24-48 hours.
                  </p>
                  <Link to="/trips">
                    <Button variant="outline">
                      Browse Our Itineraries
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Your Name *</Label>
                      <Input
                        placeholder="Full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Email *</Label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Phone</Label>
                      <Input
                        type="tel"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Type of Customization</Label>
                      <Select value={formData.tripType} onValueChange={(v) => setFormData({ ...formData, tripType: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="private_group">Private Group Tour</SelectItem>
                          <SelectItem value="custom_dates">Custom Dates</SelectItem>
                          <SelectItem value="solo_custom">Solo Custom Itinerary</SelectItem>
                          <SelectItem value="special_occasion">Special Occasion</SelectItem>
                          <SelectItem value="extended">Extended Itinerary</SelectItem>
                          <SelectItem value="fully_custom">Fully Custom Trip</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Destination Interest</Label>
                      <Select value={formData.destination} onValueChange={(v) => setFormData({ ...formData, destination: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Where do you want to go?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="egypt">Egypt</SelectItem>
                          <SelectItem value="brazil">Brazil</SelectItem>
                          <SelectItem value="morocco">Morocco</SelectItem>
                          <SelectItem value="multiple">Multiple Destinations</SelectItem>
                          <SelectItem value="open">Open to Suggestions</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Group Size</Label>
                      <Select value={formData.groupSize} onValueChange={(v) => setFormData({ ...formData, groupSize: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="How many travelers?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="solo">Just Me (Solo)</SelectItem>
                          <SelectItem value="couple">2 (Couple/Pair)</SelectItem>
                          <SelectItem value="small">3-5 people</SelectItem>
                          <SelectItem value="medium">6-10 people</SelectItem>
                          <SelectItem value="large">11-20 people</SelectItem>
                          <SelectItem value="xlarge">20+ people</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Preferred Travel Dates</Label>
                      <Input
                        placeholder="e.g., March 2025, Flexible"
                        value={formData.preferredDates}
                        onChange={(e) => setFormData({ ...formData, preferredDates: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Budget Range (per person)</Label>
                      <Select value={formData.budget} onValueChange={(v) => setFormData({ ...formData, budget: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select budget" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under2k">Under $2,000</SelectItem>
                          <SelectItem value="2k-3k">$2,000 - $3,000</SelectItem>
                          <SelectItem value="3k-5k">$3,000 - $5,000</SelectItem>
                          <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                          <SelectItem value="over10k">$10,000+</SelectItem>
                          <SelectItem value="flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-1 block">Special Occasion?</Label>
                    <Input
                      placeholder="Birthday, Anniversary, Honeymoon, etc."
                      value={formData.occasion}
                      onChange={(e) => setFormData({ ...formData, occasion: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-1 block">Tell Us About Your Dream Trip *</Label>
                    <Textarea
                      placeholder="Share your vision! What experiences are must-haves? Any special requests or requirements? The more details, the better we can customize your adventure."
                      value={formData.details}
                      onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                      rows={5}
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-secondary hover:bg-secondary/90"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Contact Alternative */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            Prefer to chat directly?{' '}
            <a href="mailto:info@circulatetravel.com" className="text-secondary hover:underline font-medium">
              Email us at info@circulatetravel.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
